import React, { useCallback, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import UserAvatar from "../../common/UserAvatar/UserAvatar";
import { uploadProfilePhoto } from "../../../api/profilePhotos";
import "./CaptainsAdminTab.css";

// Phase I. Super-admin tab for managing the captain showcase shown on
// UserDashboard's landing page. Captains live in their own
// socialCaptains DDB table — they can be standalone placeholders or
// linked to a real socialUsers row via linkedSocialUserId /
// linkedEmail. eventsHostedCount + recentlyHostedEvents are computed
// server-side at read time by matching socialEvents host strings
// against displayName (case-insensitive) + linkedEmail.

const ALL_CAPTAINS = gql`
  query AllCaptainsAdmin {
    allCaptainsAdmin {
      id
      displayName
      bio
      profileImgKey
      joinDate
      sortOrder
      active
      linkedSocialUserId
      linkedEmail
      createdAt
      createdBy
      eventsHostedCount
      recentlyHostedEvents {
        id
        title
        date
      }
    }
  }
`;

const CREATE_CAPTAIN = gql`
  mutation CreateSocialCaptain($input: SocialCaptainInput!) {
    createSocialCaptain(input: $input) {
      id
      displayName
    }
  }
`;

const UPDATE_CAPTAIN = gql`
  mutation UpdateSocialCaptain($id: ID!, $input: SocialCaptainInput!) {
    updateSocialCaptain(id: $id, input: $input) {
      id
      displayName
      bio
      profileImgKey
      joinDate
      sortOrder
      active
      linkedSocialUserId
      linkedEmail
    }
  }
`;

const DELETE_CAPTAIN = gql`
  mutation DeleteSocialCaptain($id: ID!) {
    deleteSocialCaptain(id: $id)
  }
`;

const emptyDraft = {
  displayName: "",
  bio: "",
  joinDate: "",
  sortOrder: "",
  active: true,
  linkedSocialUserId: "",
  linkedEmail: "",
  profileImgKey: null,
};

const CaptainsAdminTab = () => {
  const { data, loading, error, refetch } = useQuery(ALL_CAPTAINS, {
    fetchPolicy: "network-only",
  });
  const [createCaptain] = useMutation(CREATE_CAPTAIN);
  const [updateCaptain] = useMutation(UPDATE_CAPTAIN);
  const [deleteCaptain] = useMutation(DELETE_CAPTAIN);

  const [editingId, setEditingId] = useState(null); // null = not editing, "new" = create, "<uuid>" = edit
  const [draft, setDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const fileInputRef = useRef(null);

  const captains = data?.allCaptainsAdmin || [];

  const startCreate = () => {
    setDraft(emptyDraft);
    setEditingId("new");
    setActionMsg("");
  };

  const startEdit = (c) => {
    setDraft({
      displayName: c.displayName || "",
      bio: c.bio || "",
      joinDate: c.joinDate || "",
      sortOrder: c.sortOrder != null ? String(c.sortOrder) : "",
      active: c.active !== false,
      linkedSocialUserId: c.linkedSocialUserId || "",
      linkedEmail: c.linkedEmail || "",
      profileImgKey: c.profileImgKey || null,
    });
    setEditingId(c.id);
    setActionMsg("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const setField = (field, value) =>
    setDraft((d) => ({ ...d, [field]: value }));

  const handlePhotoClick = () => {
    if (busy) return;
    fileInputRef.current && fileInputRef.current.click();
  };

  const handlePhotoFile = useCallback(
    async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      // For new captains we don't have an id yet — upload under a
      // sentinel "captain-new-<rand>" path so the key is unique. After
      // create lands, the key already points at the captain row.
      const targetUserId =
        editingId && editingId !== "new"
          ? editingId
          : `captain-new-${Math.random().toString(36).slice(2, 10)}`;
      setBusy(true);
      setActionMsg("");
      try {
        const { s3Key } = await uploadProfilePhoto({ file, targetUserId });
        setDraft((d) => ({ ...d, profileImgKey: s3Key }));
        // If we were editing an existing row, persist immediately so
        // the showcase + table pick it up even without a full save.
        if (editingId && editingId !== "new") {
          await updateCaptain({
            variables: { id: editingId, input: { profileImgKey: s3Key } },
          });
          await refetch();
        }
      } catch (err) {
        setActionMsg(`Photo upload failed: ${err.message || err}`);
      } finally {
        setBusy(false);
      }
    },
    [editingId, updateCaptain, refetch]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draft.displayName.trim()) {
      setActionMsg("Display name is required.");
      return;
    }
    const input = {
      displayName: draft.displayName.trim(),
      bio: draft.bio.trim() || null,
      joinDate: draft.joinDate.trim() || null,
      active: draft.active,
      linkedSocialUserId: draft.linkedSocialUserId.trim() || null,
      linkedEmail: draft.linkedEmail.trim() || null,
      profileImgKey: draft.profileImgKey || null,
    };
    if (draft.sortOrder.trim()) {
      const parsed = parseInt(draft.sortOrder, 10);
      if (Number.isFinite(parsed)) input.sortOrder = parsed;
    }
    setBusy(true);
    setActionMsg("");
    try {
      if (editingId === "new") {
        await createCaptain({ variables: { input } });
        setActionMsg(`Added ${input.displayName}.`);
      } else {
        await updateCaptain({ variables: { id: editingId, input } });
        setActionMsg(`Saved ${input.displayName}.`);
      }
      await refetch();
      cancelEdit();
    } catch (err) {
      setActionMsg(`Save failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (c) => {
    if (
      !window.confirm(
        `Delete ${c.displayName}? This removes them from the showcase.`
      )
    ) {
      return;
    }
    setBusy(true);
    setActionMsg("");
    try {
      await deleteCaptain({ variables: { id: c.id } });
      setActionMsg(`Deleted ${c.displayName}.`);
      await refetch();
      if (editingId === c.id) cancelEdit();
    } catch (err) {
      setActionMsg(`Delete failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="captains-admin">
      <div className="captains-admin__header">
        <h2 className="captains-admin__title">Captains showcase</h2>
        {editingId === null && (
          <button
            type="button"
            className="captains-admin__btn captains-admin__btn--primary"
            onClick={startCreate}
          >
            + Add captain
          </button>
        )}
      </div>
      <p className="captains-admin__hint">
        Curated list shown to all users at the top of the dashboard's events
        tab. Captains here can be placeholders (no account needed) or linked
        to a real socialUsers row via the optional linked fields below — link
        an email when known so events-hosted matching can find them by email
        too.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handlePhotoFile}
        style={{ display: "none" }}
      />

      {error && (
        <div className="captains-admin__error">
          Error loading: {error.message}
        </div>
      )}
      {actionMsg && <div className="captains-admin__info">{actionMsg}</div>}

      {editingId !== null && (
        <form className="captains-admin__form" onSubmit={handleSave}>
          <div className="captains-admin__form-photo">
            <UserAvatar
              name={draft.displayName || "?"}
              email={draft.linkedEmail || draft.displayName}
              imgKey={draft.profileImgKey}
              size={96}
            />
            <button
              type="button"
              className="captains-admin__btn"
              onClick={handlePhotoClick}
              disabled={busy}
            >
              {busy
                ? "Working…"
                : draft.profileImgKey
                  ? "Change photo"
                  : "Upload photo"}
            </button>
            {draft.profileImgKey && (
              <button
                type="button"
                className="captains-admin__btn"
                onClick={() => setField("profileImgKey", null)}
                disabled={busy}
              >
                Clear
              </button>
            )}
          </div>

          <div className="captains-admin__form-grid">
            <label className="captains-admin__label">
              Display name<span className="captains-admin__req">*</span>
              <input
                type="text"
                value={draft.displayName}
                onChange={(e) => setField("displayName", e.target.value)}
                disabled={busy}
                required
              />
            </label>
            <label className="captains-admin__label">
              Join date
              <input
                type="text"
                value={draft.joinDate}
                onChange={(e) => setField("joinDate", e.target.value)}
                placeholder="e.g. May 2022 — free-form text"
                disabled={busy}
              />
            </label>
            <label className="captains-admin__label captains-admin__label--full">
              Bio
              <textarea
                value={draft.bio}
                onChange={(e) => setField("bio", e.target.value)}
                rows={3}
                placeholder="A short blurb that appears on their detail card."
                disabled={busy}
              />
            </label>
            <label className="captains-admin__label">
              Sort order
              <input
                type="number"
                value={draft.sortOrder}
                onChange={(e) => setField("sortOrder", e.target.value)}
                placeholder="lower = earlier in the strip"
                disabled={busy}
              />
            </label>
            <label className="captains-admin__label captains-admin__label--check">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setField("active", e.target.checked)}
                disabled={busy}
              />
              Show in public showcase
            </label>
            <label className="captains-admin__label">
              Linked email (optional)
              <input
                type="email"
                value={draft.linkedEmail}
                onChange={(e) => setField("linkedEmail", e.target.value)}
                placeholder="for events-hosted matching"
                disabled={busy}
              />
            </label>
            <label className="captains-admin__label">
              Linked socialUsers userId (optional)
              <input
                type="text"
                value={draft.linkedSocialUserId}
                onChange={(e) =>
                  setField("linkedSocialUserId", e.target.value)
                }
                placeholder="DDB userId"
                disabled={busy}
              />
            </label>
          </div>

          <div className="captains-admin__form-actions">
            {editingId !== "new" && (
              <button
                type="button"
                className="captains-admin__btn captains-admin__btn--danger captains-admin__btn--delete"
                onClick={() => {
                  const target = captains.find((c) => c.id === editingId);
                  if (target) handleDelete(target);
                }}
                disabled={busy}
              >
                Delete
              </button>
            )}
            <button
              type="button"
              className="captains-admin__btn"
              onClick={cancelEdit}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="captains-admin__btn captains-admin__btn--primary"
              disabled={busy}
            >
              {busy ? "Saving…" : editingId === "new" ? "Create" : "Save"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="captains-admin__info">Loading…</div>
      ) : (
        <ul className="captains-admin__list">
          {captains.length === 0 && (
            <li className="captains-admin__empty">
              No captains yet. Tap "Add captain" to create the first.
            </li>
          )}
          {captains.map((c) => (
            <li
              key={c.id}
              className={`captains-admin__row${
                c.active ? "" : " captains-admin__row--inactive"
              }`}
            >
              <button
                type="button"
                className="captains-admin__row-btn"
                onClick={() => startEdit(c)}
                disabled={busy}
                aria-label={`Edit ${c.displayName}`}
              >
                <UserAvatar
                  name={c.displayName}
                  email={c.linkedEmail || c.displayName}
                  imgKey={c.profileImgKey}
                  size={48}
                />
                <div className="captains-admin__row-main">
                  <div className="captains-admin__name">{c.displayName}</div>
                  {c.bio && (
                    <div className="captains-admin__bio-preview">{c.bio}</div>
                  )}
                  <div className="captains-admin__row-meta">
                    <span>Order: {c.sortOrder ?? "—"}</span>
                    <span>·</span>
                    <span>{c.active ? "Active" : "Hidden"}</span>
                    <span>·</span>
                    <span>Hosted: {c.eventsHostedCount}</span>
                  </div>
                </div>
                <span
                  className="captains-admin__row-chevron"
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CaptainsAdminTab;
